import {
  Button,
  Checkbox,
  Form,
  Input,
  Select,
  Space,
  Switch,
  Tooltip,
} from "antd";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import SelectDropdownLgIcon from "../../../../assets/icons/SelectDropdownLgIcon";
import Loader from "../../../../components/ui/Loader";
import {
  useCreatePermissionMutation,
  useGetPermissionDetailsQuery,
  useGetProjectWiseModuleQuery,
  useUpdatePermissionMutation,
} from "../../../../features/administrative-setting/administrativeSetting";
import { useGetAllProductQuery } from "../../../../features/portal/Products/productsApi";
import useError from "../../../../hooks/useError";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import { toast } from "../../../../lib/toast";
import { capitalize } from "../../../../utils/string";
import { selectSearchOption } from "../../../../utils/utils";

export default function UserGroupCreate() {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { updatedId, viewId } = useParams();
  const productId = Form.useWatch("productId", form);
  const {
    isLoading: isProductLoading,
    isError: isProductError,
    error: productError,
    data: getProduct,
  } = useGetAllProductQuery({
    params: {
      page: 1,
      limit: 10000,
    },
  });
  useError(isProductError, productError, "Fetch product data fail");
  // API hooks
  const [createPermission, { isLoading, isError, error, isSuccess }] =
    useCreatePermissionMutation();
  useError(isError, error, "Permission create fail");

  const [
    updatePermission,
    {
      isLoading: isUpdateLoading,
      isError: isUpdateError,
      error: updateError,
      isSuccess: isUpdateSuccess,
    },
  ] = useUpdatePermissionMutation();
  useError(isUpdateError, updateError, "Permission update fail");

  const {
    isLoading: isModuleLoading,
    isError: isModuleError,
    error: moduleError,
    data: moduleData,
  } = useGetProjectWiseModuleQuery(
    {
      params: {
        projectId: productId,
      },
    },
    {
      skip: !productId,
    }
  );
  useError(isModuleError, moduleError, "Fetch module data fail");

  const {
    isLoading: isDetailsLoading,
    isError: isDetailsError,
    error: detailsError,
    data: detailsData,
  } = useGetPermissionDetailsQuery(Number(updatedId || viewId), {
    skip: !updatedId && !viewId,
  });
  useError(isDetailsError, detailsError, "Fetch module details fail");

  // Selected permissions
  const [permissions, setPermissions] = useState<any>({});
  const [fullAccess, setFullAccess] = useState(false);

  // -------------------------------
  // FEATURE LEVEL CHECKBOX
  // -------------------------------
  const handleFeatureChange = (
    moduleId: number,
    featureId: number,
    checked: boolean
  ) => {
    setPermissions((prev: any) => {
      const existing = prev[moduleId] || [];
      const updated = checked
        ? [...existing, featureId]
        : existing.filter((id: number) => id !== featureId);

      return { ...prev, [moduleId]: updated };
    });
  };

  // -------------------------------
  // MODULE LEVEL CHECKBOX
  // -------------------------------
  const handleModuleChange = (moduleObj: any, checked: boolean) => {
    setPermissions((prev: any) => {
      return {
        ...prev,
        [moduleObj.id]: checked
          ? moduleObj.projectModuleFeatures.map((f: any) => f.id)
          : [],
      };
    });
  };

  // -------------------------------
  // FULL ACCESS TOGGLE MANUAL CLICK
  // -------------------------------
  const handleFullAccess = (checked: boolean) => {
    setFullAccess(checked);

    if (checked) {
      const all: any = {};
      moduleData?.data?.modules?.forEach((m) => {
        all[m.id] = m.projectModuleFeatures.map((f: any) => f.id);
      });
      setPermissions(all);
    } else {
      setPermissions({});
    }
  };

  // -------------------------------
  // AUTO-DETECT FULL ACCESS
  // -------------------------------
  useEffect(() => {
    if (!moduleData?.data?.modules) return;

    const all: any = {};
    moduleData.data.modules.forEach((m: any) => {
      all[m.id] = m.projectModuleFeatures.map((f: any) => f.id);
    });

    const allJson = JSON.stringify(all);
    const currentJson = JSON.stringify(permissions);

    setFullAccess(allJson === currentJson);
  }, [permissions, moduleData]);

  // -------------------------------
  // SUBMIT FORM
  // -------------------------------
  const onFinish = (values: any) => {
    const formatted = Object.keys(permissions).map((moduleId) => ({
      moduleId: Number(moduleId),
      projectModuleFeaturesId: permissions[moduleId],
    }));

    if (updatedId) {
      updatePermission({
        id: updatedId,
        body: { ...values, modules: formatted },
      });
      return;
    }

    createPermission({
      ...values,
      modules: formatted,
    });
  };

  // Success handlers
  useEffect(() => {
    if (isSuccess) {
      toast("success", "Module created successfully");
      navigate("/admin/user-access-permission/user-group");
    }
  }, [isSuccess, navigate]);

  useEffect(() => {
    if (isUpdateSuccess) {
      toast("success", "Module updated successfully");
      navigate("/admin/user-access-permission/user-group");
    }
  }, [isUpdateSuccess, navigate]);

  // Prefill form for edit
  const details = detailsData?.data?.enhancedPermission;

  useEffect(() => {
    if (details) {
      form.setFieldsValue({
        groupName: details.groupName,
        productId: details.productId,
      });
    }
  }, [details, form]);

  // Prefill permissions for edit
  useEffect(() => {
    if (details?.modulesFeatures && moduleData?.data?.modules) {
      const preSelected: any = {};

      details.modulesFeatures.forEach((moduleItem: any) => {
        preSelected[moduleItem.moduleId] = moduleItem.projectModuleFeaturesId;
      });

      setPermissions(preSelected);
    }
  }, [details, moduleData]);

  return (
    <SidebarLayout title="User Access Permission">
      {isDetailsLoading ? (
        <Loader />
      ) : (
        <div className={`border border-light/50 rounded bg-white p-4`}>
          <h6 className="text-header text-3xl font-semibold leading-10">
            Set Permissions
          </h6>
          <p className="text-body-text text-sm">
            Toggle access rights for each module below.
          </p>

          <Form
            className="custom-form"
            layout="vertical"
            requiredMark="optional"
            size="large"
            onFinish={onFinish}
            form={form}
          >
            {/* SELECT PRODUCT + GROUP NAME */}
            <div className="grid md:grid-cols-2 gap-x-4 pt-6 pb-2">
              <Form.Item
                name="productId"
                label="Select Product"
                className="mb-4"
                rules={[{ required: true, message: "Please select product" }]}
              >
                <Select
                  placeholder="Select"
                  size="large"
                  allowClear
                  showSearch
                  filterOption={selectSearchOption}
                  options={getProduct?.data?.data?.map((el) => ({
                    label: el?.name,
                    value: el?.id,
                  }))}
                  suffixIcon={<SelectDropdownLgIcon />}
                  disabled={!!viewId}
                  loading={isProductLoading}
                />
              </Form.Item>

              <Form.Item
                name="groupName"
                label="Group Name"
                className="mb-4"
                rules={[{ required: true, message: "Please enter group name" }]}
              >
                <Input
                  size="large"
                  placeholder="Enter group name"
                  disabled={!!viewId}
                />
              </Form.Item>
            </div>

            {/* FULL ACCESS */}
            <div className="px-4 py-3 bg-orange-50/25 rounded-md outline outline-1 outline-offset-[-1px] outline-red-200 flex justify-between items-center">
              <h6 className="text-sub-header text-lg font-semibold">
                Full Access to All Modules
              </h6>

              <Switch
                checked={fullAccess}
                onChange={handleFullAccess}
                disabled={!!viewId}
              />
            </div>

            {/* MODULES + FEATURES */}
            {isModuleLoading ? (
              <Loader />
            ) : (
              <div className="grid md:grid-cols-3 gap-4 mt-4">
                {productId &&
                  moduleData?.data?.modules?.map((module) => {
                    const moduleFeatures = module?.projectModuleFeatures?.map(
                      (f) => f?.id
                    );
                    const selectedFeatures = permissions[module.id] || [];

                    const isModuleChecked =
                      selectedFeatures?.length === moduleFeatures?.length;

                    return (
                      <div
                        className="p-3 bg-white rounded-lg outline outline-1 outline-offset-[-1px] outline-slate-100"
                        key={module?.id}
                      >
                        <div className="mb-3 flex justify-between items-center">
                          <h6 className="text-lg font-medium flex items-center gap-1.5">
                            {module?.platform && (
                              <Tooltip title={capitalize(module?.platform)}>
                                <img
                                  src={
                                    module?.platform === "web"
                                      ? "/permission-icons/web.png"
                                      : "/permission-icons/app.png"
                                  }
                                  alt=""
                                  className="size-5"
                                />
                              </Tooltip>
                            )}
                            {module.name}
                          </h6>

                          <Checkbox
                            checked={isModuleChecked}
                            onChange={(e) =>
                              handleModuleChange(module, e.target.checked)
                            }
                            disabled={!!viewId}
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                          {module.projectModuleFeatures.map((feature) => {
                            const isChecked = permissions[module.id]?.includes(
                              feature.id
                            );

                            return (
                              <div
                                key={feature.id}
                                className="px-2.5 py-1.5 bg-[#F9FAFB] rounded-md flex items-center gap-x-1.5"
                              >
                                <Checkbox
                                  checked={isChecked}
                                  onChange={(e) =>
                                    handleFeatureChange(
                                      module.id,
                                      feature.id,
                                      e.target.checked
                                    )
                                  }
                                  disabled={!!viewId}
                                />

                                <span className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-extra-gray">
                                  <span className="size-4 flex items-center justify-center">
                                    <img
                                      src={`/permission-icons/${feature.featureIcon}`}
                                      alt="."
                                    />
                                  </span>
                                  {feature.name}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}

            {/* SUBMIT BUTTONS */}
            <Space className="w-full mt-2" size={12}>
              <Button
                className="normal-dark-button modal-button"
                onClick={() => navigate(-1)}
                size="middle"
              >
                Cancel
              </Button>
              {!viewId && (
                <Button
                  className="modal-button"
                  type="primary"
                  htmlType="submit"
                  size="middle"
                  loading={isLoading || isUpdateLoading}
                >
                  {updatedId ? "Update" : "Create"}
                </Button>
              )}
            </Space>
          </Form>
        </div>
      )}
    </SidebarLayout>
  );
}
